import React, { useState } from 'react';
import { BarcodeIcon, CheckCircle2Icon, FileTextIcon, MessageSquareIcon, PrinterIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { DemoUser } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { Modal } from '../../components/shared/Modal';
import { CustodyReceipt } from '../../components/print/CustodyReceipt';
import { categoryLabel } from '../../utils/caseStatus';
import { fmtDate, fmtDateTime, maskMobile, taka } from '../../utils/bn';
export function ReceiptScreen({
  pcr,
  user,
  go
}: {
  pcr: string;
  user: DemoUser;
  go: (s: FieldScreen) => void;
}) {
  const {
    getCase,
    config,
    updateCase
  } = useStore();
  const c = getCase(pcr);
  const [preview, setPreview] = useState<'print' | 'pdf' | null>(null);
  const [sms, setSms] = useState(false);
  if (!c) return <p className="p-6" data-id="element-2154" data-mp="oeyv7t1spe96">কেস পাওয়া যায়নি।</p>;
  const sendSms = () => {
    updateCase(pcr, x => x, {
      key: 'sms',
      actor: user.name,
      role: user.title,
      action: 'মালিককে SMS প্রেরণ (সিমুলেটেড)'
    });
    setSms(false);
    toast.success(`${maskMobile(c.mobile)} নম্বরে SMS পাঠানো হয়েছে (সিমুলেটেড)।`);
  };
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="যানবাহন হেফাজত রসিদ" onBack={() => go({
      name: 'home'
    })} data-id="element-2155" data-mp="334j11tqak6w0" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-2156" data-mp="4lc7c97ujwjm">
        <div className="flex items-center gap-3 rounded-xl bg-success-50 border border-success/20 p-3" data-id="element-2157" data-mp="3vs58jd2n3vo5">
          <CheckCircle2Icon className="h-8 w-8 text-success" data-id="element-2158" data-mp="3r16oq3ig0fg5" />
          <div data-id="element-2159" data-mp="3qzltscs7uuhr">
            <p className="text-sm font-bold text-success" data-id="element-2160" data-mp="3m353bbxjo41i">PCR তৈরি সম্পন্ন</p>
            <p className="text-xs text-ink-muted" data-id="element-2161" data-mp="3m11251r15j4x">কেন্দ্রীয় রেকর্ডে যুক্ত — ওয়েব পোর্টালে দৃশ্যমান</p>
          </div>
        </div>
        <div className="card overflow-hidden" data-id="element-2162" data-mp="ilmxtb58v949">
          <div className="bg-navy px-4 py-2.5 text-white flex justify-between" data-id="element-2163" data-mp="iku0t1w37xzb">
            <span className="font-mono text-sm font-bold" data-id="element-2164" data-mp="2cvaby47u45g7">{c.pcr}</span>
            <span className="text-xs text-white/75" data-id="element-2165" data-mp="2gvu5kxt78qxl">রসিদ: {c.receiptNo}</span>
          </div>
          <dl className="divide-y divide-line text-sm" data-id="element-2166" data-mp="234pfambq1azx">
            {[['রেজিস্ট্রেশন', c.regNo], ['ধরন', categoryLabel(c.category, config)], ['মালিক / চালক', `${c.ownerName} / ${c.driverName || '—'}`], ['যোগাযোগ', c.mobile ? maskMobile(c.mobile) : '—'], ['আটকের কারণ', c.reason], ['আটক স্থান', c.road], ['তারিখ ও সময়', fmtDateTime(c.createdAt)], ['ট্রাফিক সার্জেন্ট', c.sergeant], ['রেকার অপারেটর', `${c.operator} (${c.wreckerId})`], ['জরিমানা (নমুনা)', taka(c.fine + c.extraCharge)], ['কাগজপত্র জমার শেষ তারিখ', fmtDate(c.deadline)]].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-2167" data-mp="3jh4gdvh7yvji"><dt className="text-ink-muted shrink-0" data-id="element-2168" data-mp="3tm4btywvmoy6">{k}</dt><dd className="text-right font-semibold" data-id="element-2169" data-mp="3tmvwwfsul4a7">{v}</dd></div>)}
          </dl>
        </div>
        <div className="card p-4" data-id="element-2170" data-mp="3op47bsfodu96">
          <p className="text-sm font-bold text-navy" data-id="element-2171" data-mp="691p5k8l255c">যানবাহন অবমুক্তির জন্য করণীয়</p>
          <ol className="mt-1.5 list-decimal pl-5 text-xs text-ink-muted space-y-0.5" data-id="element-2172" data-mp="6b4s14uu3srh">
            <li data-id="element-2173" data-mp="1ggyusovya6k">নির্ধারিত সময়ের মধ্যে প্রয়োজনীয় কাগজপত্র জমা দিন।</li>
            <li data-id="element-2174" data-mp="g5wmhzt4dzi1">অনলাইনে অথবা নির্ধারিত অফিসে জরিমানা পরিশোধ করুন।</li>
            <li data-id="element-2175" data-mp="b9677ep537xo">ডিএমপি কর্তৃপক্ষের যাচাই ও অনুমোদনের জন্য অপেক্ষা করুন।</li>
            <li data-id="element-2176" data-mp="p85zcwbv24yl">অনুমোদিত অবমুক্তি পত্র সংগ্রহ করুন।</li>
            <li data-id="element-2177" data-mp="p7meb6qwz0gc">ডাম্পিং স্টেশনে পত্র প্রদর্শন করে যানবাহন গ্রহণ করুন।</li>
          </ol>
        </div>
      </div>
      <div className="shrink-0 border-t border-line bg-white p-3 grid grid-cols-2 gap-2" data-id="element-2178" data-mp="l7cbg2yf4q5p">
        <button className="btn btn-outline" onClick={() => setPreview('print')} data-id="element-2179" data-mp="2qzsr503ku3w4"><PrinterIcon className="h-4 w-4" data-id="element-2180" data-mp="2myz408xykt4a" /> রসিদ প্রিন্ট করুন</button>
        <button className="btn btn-outline" onClick={() => setPreview('pdf')} data-id="element-2181" data-mp="2ofcv8zacxw78"><FileTextIcon className="h-4 w-4" data-id="element-2182" data-mp="2oh77lp7kcfzp" /> PDF দেখুন</button>
        <button className="btn btn-outline" onClick={() => setSms(true)} disabled={!c.mobile} data-id="element-2183" data-mp="2td4p3yukbse2"><MessageSquareIcon className="h-4 w-4" data-id="element-2184" data-mp="2xfj4ua2xqc14" /> মালিককে SMS পাঠান</button>
        <button className="btn btn-green" onClick={() => go({
        name: 'sticker',
        pcr
      })} data-id="element-2185" data-mp="32em052t453vw"><BarcodeIcon className="h-4 w-4" data-id="element-2186" data-mp="32crmm9k68e1u" /> বারকোড তৈরি করুন</button>
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview === 'pdf' ? 'PDF প্রিভিউ — হেফাজত রসিদ' : 'প্রিন্ট প্রিভিউ — হেফাজত রসিদ'} size="lg" footer={<>
          <button className="btn btn-outline" onClick={() => setPreview(null)} data-id="element-2188" data-mp="2eohyv23edg82">বন্ধ করুন</button>
          <button className="btn btn-primary" onClick={() => {
        updateCase(pcr, x => x, {
          key: 'receipt',
          actor: user.name,
          role: user.title,
          action: 'হেফাজত রসিদ প্রিন্ট'
        });
        window.print();
      }} data-id="element-2189" data-mp="2jgrtuwjdd6h7"><PrinterIcon className="h-4 w-4" data-id="element-2190" data-mp="1qynkuh7cdlm8" /> প্রিন্ট</button>
        </>} data-id="element-2187" data-mp="25qg0yveyn3p0">
        <CustodyReceipt c={c} config={config} data-id="element-2191" data-mp="1jfxts5seedbq" />
      </Modal>

      <Modal open={sms} onClose={() => setSms(false)} title="SMS প্রিভিউ" contained footer={<button className="btn btn-green" onClick={sendSms} data-id="element-2193" data-mp="1ehwb3lyvwg0s">পাঠান (সিমুলেটেড)</button>} data-id="element-2192" data-mp="1jfel7fon11ad">
        <div className="rounded-2xl rounded-bl-sm bg-canvas p-3 text-sm leading-relaxed" data-id="element-2194" data-mp="19gfbruw1t1mb">
          ডিএমপি: আপনার যানবাহন {c.regNo} {fmtDate(c.createdAt)} তারিখে হেফাজতে নেওয়া হয়েছে। PCR: {c.pcr}। জরিমানা {taka(c.fine)}। {fmtDate(c.deadline)}-এর মধ্যে কাগজপত্র জমা দিন। সেবা: ডিএমপি যানবাহন সেবা অ্যাপ।
        </div>
        <p className="mt-2 text-xs text-ink-muted" data-id="element-2195" data-mp="voin60tl99uo">প্রাপক: {maskMobile(c.mobile)} · কোনো প্রকৃত SMS গেটওয়ে সংযুক্ত নয়।</p>
      </Modal>
    </>;
}